import { useCallback, useState } from 'react';
import { createInvite } from './api';
import type { InviteLink } from './api';

/**
 * "Invite guest" — mints a link and puts it on the clipboard.
 *
 * Hidden entirely from guests: the portal refuses them anyway, and offering a
 * button that always errors is worse than not offering it.
 *
 * The link is shown as selectable text as well as copied, because clipboard
 * writes fail silently in plenty of situations (permissions, non-secure context,
 * some mobile browsers) and "I clicked it and nothing happened" is a bad outcome
 * for the one feature whose whole job is to hand someone a URL.
 */
export function InviteButton({ room, canInvite }: { room: string; canInvite: boolean }) {
  const [link, setLink] = useState<InviteLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const mint = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createInvite(room);
      setLink(created);
      try {
        await navigator.clipboard.writeText(created.url);
        setCopied(true);
      } catch {
        setCopied(false); // the URL is on screen regardless
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [room]);

  if (!canInvite) return null;

  return (
    <div className="mx-invite">
      <button className="mx-invite-btn" onClick={() => void mint()} disabled={busy}>
        {busy ? 'Creating…' : 'Invite guest'}
      </button>

      {(link || error) && (
        <div className="mx-invite-pop" role="status">
          {error && <p className="mx-error">{error}</p>}
          {link && (
            <>
              <p className="mx-muted">
                {copied ? 'Link copied to your clipboard.' : 'Copy this link and send it:'}
              </p>
              <input
                className="mx-invite-url"
                readOnly
                value={link.url}
                onFocus={(e) => e.currentTarget.select()}
              />
              <p className="mx-muted">
                Expires {new Date(link.expiresAt).toLocaleString()}. Anyone with the link can join
                this meeting until then.
              </p>
              <button className="mx-invite-close" onClick={() => setLink(null)}>
                Done
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
